import i18next, { ResourceLanguage } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Message, setLocale as setYupLocale } from 'yup';
import LanguageDetector, {
  DetectorOptions,
} from 'i18next-browser-languagedetector';
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend';

import common from './common';

import moment from 'moment-timezone';
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/de';

import {
  toEuros,
  toCurrency,
  toPercent,
  formatSiret,
  formatVat,
} from './formatters';
import { useEffect } from 'react';
import { currentCurrencySign } from './currency';

export * from './dates';
export { toEuros, toCurrency, toPercent, formatSiret, formatVat };

export const createI18n = (ns: string, resource: ResourceLanguage = {}) => {
  i18next
    .use(HttpBackend)
    .use(LanguageDetector)
    .init<DetectorOptions & HttpBackendOptions>({
      ns,
      fallbackLng: 'en',
      defaultNS: ns,
      fallbackNS: ['common'],
      partialBundledLanguages: true,
      // strictKeyChecks: true,
      // enableSelector: true,
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
      resources: {
        ...common,
        en: {
          ...resource,
          common: common.en.common,
        },
      },
      backend: {
        loadPath: '/assets/translations/{{lng}}/{{ns}}.json',
      },
      detection: {
        order: ['querystring', 'navigator', 'htmlTag', 'path', 'subdomain'],
        lookupQuerystring: 'source_locale',
        caches: [],
      },
    });

  setLocale(getLocale(), i18next);

  if (i18next.services.formatter) {
    const formatter = i18next.services.formatter;
    formatter.add('currency', (value, locale, options) => {
      return toCurrency(
        value,
        !options.showCents,
        options.currency || 'EUR',
        locale,
        options,
      );
    });

    formatter.add('euros', (value, locale, options) => {
      return toEuros(value, !options.showCents, locale, options);
    });
    formatter.add('eurosNoCents', (value, locale, options) => {
      return toEuros(value, true, locale, options);
    });
    formatter.add('priceNoCurrency', (value, locale, options) => {
      return toCurrency(value, false, undefined, locale, options);
    });
    formatter.add('priceNoCurrencyNoCents', (value, locale, options) => {
      return toCurrency(value, true, undefined, locale, options);
    });
    formatter.add('lowercase', (value) => {
      return value.toLowerCase();
    });
    formatter.add('ucfirst', (value) => {
      if (!value) {
        return '';
      }
      return value[0].toUpperCase() + value.slice(1);
    });
    formatter.add('month2Digits', (value) => {
      const mres = `${value}`;
      if (mres.length === 1) {
        return `0${mres}`;
      }
      return mres;
    });
    formatter.add('year2Digits', (value) => {
      const yres = `${value}`;
      if (yres.length > 2) {
        return yres.slice(-2);
      }
      return yres;
    });
    formatter.add(
      'date',
      (value: any, locale?: string, params: Record<string, any> = {}) => {
        return Intl.DateTimeFormat(locale, {
          timeZone: params['timezone'],
          ...params,
        }).format(moment(value).toDate());
      },
    );
    formatter.add('number', (value, locale, options) => {
      return Intl.NumberFormat(locale, options).format(value);
    });
    formatter.add('list', (value) => {
      return Array.isArray(value) ? value.join(', ') : value;
    });
    formatter.add('currency_sign', () => {
      return currentCurrencySign;
    });
  }

  return i18next;
};

export const I18n = i18next;
export const i18n = i18next;

export const formatLocale = (locale: string) =>
  locale.substring(0, 2).toLowerCase();

export const setLocale = (
  localeParams: string = 'en',
  i18nInstance?: typeof I18n,
) => {
  const locale = formatLocale(localeParams);
  if (i18nInstance) {
    i18nInstance.changeLanguage(locale);
  } else {
    i18next.changeLanguage(locale);
  }
  moment.locale(locale);
};

export const getLocale = (i18n?: typeof i18next) => {
  const lib = i18n || i18next;
  return lib.resolvedLanguage || lib.language;
};
export const useLocale = (locale?: string, i18nInstance?: typeof I18n) => {
  const { i18n } = useTranslation('common');
  useEffect(() => {
    if (!locale) {
      return;
    }
    setLocale(locale, i18nInstance || i18n);
  }, [locale, i18nInstance, i18n]);
  return getLocale();
};

const getYupTrans =
  (errorName: string): Message<any> =>
  (yupParams) => {
    const completePath = yupParams.path || '';
    const name = completePath.split('.').pop();

    return {
      name,
      errorName,
      ...yupParams,
    };
  };

setYupLocale({
  mixed: {
    default: getYupTrans('invalid'),
    required: getYupTrans('required'),
    notType: getYupTrans('notType'),
  },
  string: {
    min: getYupTrans('tooShort'),
    max: getYupTrans('tooLong'),
    email: getYupTrans('invalidEmail'),
    matches: getYupTrans('invalid'),
    url: getYupTrans('url'),
  },
  number: {
    min: getYupTrans('min'),
    max: getYupTrans('max'),
  },
  array: {
    min: getYupTrans('arrayMin'),
    max: getYupTrans('arrayMax'),
  },
});

moment.updateLocale('fr', {
  longDateFormat: {
    l: 'DD/MM',
    ll: 'D MMMM',
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'dddd D MMMM YYYY',
    LLLL: 'dddd D MMMM YYYY à HH:mm',
  },
});

moment.updateLocale('en', {
  longDateFormat: {
    l: 'MM/DD',
    ll: 'MMMM D',
    LT: 'h:mm A',
    LTS: 'h:mm:ss A',
    L: 'MM/DD/YYYY',
    LL: 'MMMM D, YYYY',
    LLL: 'dddd, MMMM D, YYYY',
    LLLL: 'dddd, MMMM D, YYYY h:mm A',
  },
});

moment.updateLocale('es', {
  longDateFormat: {
    l: 'DD/MM',
    ll: 'D MMMM',
    LT: 'H:mm',
    LTS: 'H:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D [de] MMMM [de] YYYY',
    LLL: 'D [de] MMMM [de] YYYY',
    LLLL: 'dddd, D [de] MMMM [de] YYYY H:mm',
  },
});

moment.updateLocale('it', {
  longDateFormat: {
    l: 'DD/MM',
    ll: 'D MMMM',
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'dddd D MMMM YYYY',
    LLLL: 'dddd D MMMM YYYY HH:mm',
  },
});

moment.updateLocale('de', {
  longDateFormat: {
    l: 'DD/MM',
    ll: 'D MMMM',
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD.MM.YYYY',
    LL: 'D. MMMM YYYY',
    LLL: 'dddd, D. MMMM YYYY',
    LLLL: 'dddd, D. MMMM YYYY HH:mm',
  },
});

moment.tz.setDefault(moment.tz.guess());
